import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useAuthStore from './stores/authStore';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';
import { HistoryProvider } from './contexts/HistoryContext';
import { OrdersProvider } from './contexts/OrdersContext';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';

// App Pages. The default page (inventory) stays in the entry chunk; the
// others are route-level lazy chunks so first load doesn't ship recharts,
// the sync timeline, etc.
import InventoryPage from './pages/InventoryPage';
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const VentesPage = lazy(() => import('./pages/VentesPage'));
const VersionPage = lazy(() => import('./pages/VersionPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SyncTimelinePage = lazy(() => import('./pages/SyncTimelinePage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const MobileDepotPage = lazy(() => import('./pages/MobileDepotPage'));

function PageLoader() {
  return (
    <div className="flex h-full min-h-[40vh] items-center justify-center text-muted-foreground">
      Chargement…
    </div>
  );
}

interface RouteWrapperProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: RouteWrapperProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function PublicRoute({ children }: RouteWrapperProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  const { i18n } = useTranslation();
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <ErrorBoundary>
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <HistoryProvider>
        <OrdersProvider>
          <Routes>
            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <LoginPage />
                  </AuthLayout>
                </PublicRoute>
              }
            />

            {/* Dépôt mobile : ouvert par le QR du poste de travail. Public par
                design — le jeton à portée unique dans le fragment d'URL EST
                l'autorisation ; exiger un login ici tuerait l'usage. */}
            <Route
              path="/depot"
              element={<Suspense fallback={<PageLoader />}><MobileDepotPage /></Suspense>}
            />

            {/* Protected App Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<InventoryPage />} />
              <Route path="inventaire" element={<InventoryPage />} />
              <Route path="commande" element={<Suspense fallback={<PageLoader />}><OrdersPage /></Suspense>} />
              <Route path="ventes" element={<Suspense fallback={<PageLoader />}><VentesPage /></Suspense>} />
              <Route path="changelog" element={<Suspense fallback={<PageLoader />}><VersionPage /></Suspense>} />
              <Route path="demarrage" element={<Suspense fallback={<PageLoader />}><OnboardingPage /></Suspense>} />
              <Route path="synchronisation" element={<Suspense fallback={<PageLoader />}><SyncTimelinePage /></Suspense>} />
              <Route path="parametres" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </OrdersProvider>
      </HistoryProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
